/**
 * CONTAS — o que pagar e o que receber, por vencimento (R2B, revisão de
 * 06/10/2026). A agenda do escritório: a esposa ou a secretária (Consulta) e o
 * gestor abrem aqui e veem, numa lista só, os vendedores, os fretes e gastos,
 * a Comissão do comprador (você paga), a Comissão do cliente (você recebe), as
 * vendas e as revendas a receber.
 *
 * Os números saem de `contasDasFichas` (src/dominio/financeiro.ts) — a mesma
 * conta das Fichas, da aba Pagamento, do Custo, do Relatório e do Excel.
 *
 * «Pago hoje»: o que já saiu hoje, por conta de origem — para não passar do
 * limite diário do banco. A baixa (pagou, recebeu) se dá dentro da ficha, na
 * aba Pagamento (ou Custo, para o frete): um toque na linha abre a ficha.
 *
 * Quem vê: gestor e Consulta, com a ferramenta «Comissões e pagamentos»
 * ligada. O comprador não chega aqui (as comissões e as vendas não são dele).
 * Funciona sem sinal: é o que está no aparelho.
 */
import { useMemo, useState } from 'react'
import { usarEstado } from '../dados/armazem'
import { usarPagamentos } from '../dados/pagamentos'
import type { Eu } from '../dados/sessao'
import { contasDasFichas, dataDaConta, NOMES_DAS_CONTAS, pagoNoDia, resumirContas, type ContaFinanceira } from '../dominio/financeiro'
import { dataBR, hoje, reais } from '../dominio/numeros'
import { pode } from '../dominio/permissoes'
import type { Produtor } from '../dominio/tipos'
import { Dica, Segmento, type Ir } from '../ui'

type Filtro = 'pagar' | 'receber' | 'vencidas' | 'tudo'

export function Contas({ eu, ir }: { eu: Eu; ir: Ir }) {
  const { fichas, produtores } = usarEstado()
  const pg = usarPagamentos()
  const [filtro, setFiltro] = useState<Filtro>('pagar')
  const [verQuitadas, setVerQuitadas] = useState(false)
  const dia = hoje()
  const verComissao = pode(eu.pessoa.papel, 'verComissao')
  const todas = useMemo(() => contasDasFichas(fichas, pg, { verComissao }), [fichas, pg, verComissao])
  const resumo = resumirContas(todas, dia)
  const saiuHoje = pagoNoDia(fichas, pg, dia)

  // o comprador não chega aqui (o botão não aparece), mas a tela confere de novo
  if (!verComissao) return <div className="cartao vazio">As contas são do gestor e da Consulta.</div>

  const lista = todas
    .filter((c) => (verQuitadas ? true : c.falta > 0))
    .filter((c) => filtro === 'tudo' ? true : filtro === 'vencidas' ? c.falta > 0 && !!c.vencimento && c.vencimento < dia : c.sentido === filtro)
    .sort((a, b) => dataDaConta(a).localeCompare(dataDaConta(b)) || a.numero.localeCompare(b.numero))
  const doFiltro = resumirContas(lista, dia)
  const cadastro = new Map(produtores.map((p) => [p.id, p]))

  return (
    <div>
      <h2 className="secao">Contas a pagar e a receber</h2>
      <Dica>Tudo o que falta pagar e receber das fichas encerradas, por vencimento. Para dar baixa, toque na linha: abre a ficha.
        Sincronize antes, para entrar o que foi lançado em outros aparelhos.</Dica>

      <div className="painel">
        <button className={filtro === 'pagar' ? 'on' : ''} onClick={() => setFiltro('pagar')}><b>{reais(resumo.aPagar)}</b><span>a pagar ({resumo.nPagar})</span></button>
        <button className={filtro === 'receber' ? 'on' : ''} onClick={() => setFiltro('receber')}><b>{reais(resumo.aReceber)}</b><span>a receber ({resumo.nReceber})</span></button>
        <button className={filtro === 'vencidas' ? 'on' : ''} onClick={() => setFiltro('vencidas')}><b>{resumo.vencidas}</b><span>vencidas</span></button>
      </div>

      {/* o limite diário do banco: o que já saiu hoje */}
      <div className="cartao">
        <div className="tl"><span><b>Pago hoje</b> ({dataBR(dia)})</span><b>{reais(saiuHoje.total)}</b></div>
        {saiuHoje.porConta.length > 1 || (saiuHoje.porConta[0] && saiuHoje.porConta[0].conta) ? saiuHoje.porConta.map((x) => (
          <div className="tl" key={x.conta}><span className="mini-dica" style={{ marginTop: 0 }}>{x.conta || 'conta não informada'}</span><span>{reais(x.valor)}</span></div>
        )) : null}
        <div className="mini-dica">Soma o que saiu hoje: pagamentos aos vendedores, fretes e gastos pagos e comissão paga fora do acerto — para não passar do limite diário do banco.
          Informe a «conta de origem» ao lançar o pagamento para ver a soma por conta.</div>
      </div>

      <div className="cartao">
        <Segmento<Filtro> valor={filtro} mudar={setFiltro} opcoes={[['pagar', 'A pagar'], ['receber', 'A receber'], ['vencidas', 'Vencidas'], ['tudo', 'Tudo']]} />
        <label className="campo" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <input type="checkbox" checked={verQuitadas} onChange={(e) => setVerQuitadas(e.target.checked)} style={{ width: 'auto' }} />
          <span style={{ margin: 0 }}>Mostrar também as já quitadas</span>
        </label>
        <div className="mini-dica">{lista.length} conta{lista.length === 1 ? '' : 's'}
          {filtro !== 'receber' && doFiltro.aPagar > 0 && ` · a pagar ${reais(doFiltro.aPagar)}`}
          {filtro !== 'pagar' && doFiltro.aReceber > 0 && ` · a receber ${reais(doFiltro.aReceber)}`}</div>
      </div>

      {lista.length
        ? lista.map((c) => {
          const f = fichas.find((x) => x.id === c.fichaId)
          const prod = f ? cadastro.get(f.produtor.id) ?? f.produtor : undefined
          return <LinhaDaConta key={c.id} c={c} dia={dia} produtor={c.tipo === 'vendedor' ? prod : undefined}
            abrir={() => ir({ tela: 'ficha', id: c.fichaId, aba: c.tipo === 'gasto' ? 'custo' : 'pagamento' })} />
        })
        : <div className="cartao vazio">{filtro === 'vencidas' ? 'Nenhuma conta vencida.' : 'Nada em aberto aqui.'}</div>}
      {resumo.aMais > 0 && <div className="cartao"><div className="tl"><span>Pago ou recebido a mais (confira nas fichas)</span><b>{reais(resumo.aMais)}</b></div></div>}
    </div>
  )
}

function LinhaDaConta({ c, dia, produtor, abrir }: { c: ContaFinanceira; dia: string; produtor?: Partial<Produtor>; abrir: () => void }) {
  const vencida = c.falta > 0 && !!c.vencimento && c.vencimento < dia
  const banco = produtor && [produtor.pix ? `PIX ${produtor.pix}` : '', [produtor.banco, produtor.agencia ? `ag. ${produtor.agencia}` : '', produtor.conta ? `c/c ${produtor.conta}` : ''].filter(Boolean).join(' · ')]
    .filter(Boolean).join(' — ')
  return (
    <div className="item clicavel cartao" onClick={abrir}>
      <div className="item-topo">
        <span className="item-titulo">{c.quem}</span>
        <span className="item-valor" style={{ color: c.sentido === 'receber' ? 'var(--verde)' : 'var(--texto)' }}>{c.sentido === 'receber' ? '+ ' : ''}{reais(c.falta > 0 ? c.falta : c.valor)}</span>
      </div>
      <div className="item-baixo">
        <span>{NOMES_DAS_CONTAS[c.tipo]} · Nº {c.numero}{['gasto', 'revenda'].includes(c.tipo) || c.descricao.includes('a cargo') ? ` · ${c.descricao}` : ''}</span>
      </div>
      <div className="etiquetas">
        <span>
          <span className={`etiqueta ${c.sentido === 'pagar' ? 'neutra' : 'ok'}`}>{c.sentido === 'pagar' ? 'a pagar' : 'a receber'}</span>
          {c.vencimento
            ? <span className={`etiqueta ${vencida ? 'cancelada' : 'aberta'}`}>{vencida ? 'venceu' : 'vence'} {dataBR(c.vencimento)}</span>
            : <span className="etiqueta neutra" title="sem vencimento combinado: vale a data da ficha">sem vencimento · ficha de {dataBR(c.data)}</span>}
          {c.pago > 0 && c.falta > 0 && <span className="etiqueta aberta">{c.sentido === 'pagar' ? 'pago' : 'recebido'} {reais(c.pago)} de {reais(c.valor)}</span>}
          {c.quitada && <span className="etiqueta ok">{c.sentido === 'pagar' ? 'pago' : 'recebido'}</span>}
          {c.aMais > 0 && <span className="etiqueta cancelada">a mais {reais(c.aMais)}</span>}
        </span>
      </div>
      {banco && <div className="mini-dica">{banco}</div>}
    </div>
  )
}
